import {
    DoCheck,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    Optional,
    Renderer2,
    Self,
    SimpleChanges,
    OnInit,
    ChangeDetectorRef,
    Component,
    ViewEncapsulation,
    HostListener,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputDirective } from '../../input.directive';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'input[euiInputRadio]',
    styleUrls: ['./styles/_index.scss'],
    template: '',
    encapsulation: ViewEncapsulation.None,
})
export class EuiInputRadioComponent extends InputDirective implements OnInit, DoCheck, OnChanges, ControlValueAccessor {

    @Input()
    public get isInvalid() {
        return this._isInvalid || null;
    }
    public set isInvalid(state: any) {
        this.setInvalid(state);
    }
    protected _isInvalid: boolean;

    @HostBinding('class')
    public get cssClasses(): any {
        return [
            this.getCssClasses('eui-input-radio'),
            this._isInvalid ? 'eui-input-radio--invalid' : '',
            this._hasLabel ? '' : 'eui-input-radio--nolabel',
        ].join(' ').trim();
    }

    @Input() label: string;
    protected _labelRef: HTMLLabelElement;

    @Input() styleClass: string;
    @HostBinding('attr.type') protected type: string = 'radio';

    @HostBinding('attr.checked')
    @Input('checked')
    public get defaultChecked(): boolean {
        return this._defaultChecked;
    }
    public set defaultChecked(value: boolean) {
        this._defaultChecked = coerceBooleanProperty(value);
    }
    protected _defaultChecked: boolean;

    public get selected(): boolean {
        return this._elementRef?.nativeElement.checked;
    }
    private _hasLabel: boolean;

    constructor(
        @Optional() @Self() protected ngControl: NgControl,
        protected _elementRef: ElementRef<HTMLInputElement>,
        protected _renderer: Renderer2,
        private _cd: ChangeDetectorRef,
    ) {
        super(_elementRef, _renderer, ngControl);

        // Firefox fix: set type to radio before first ngDoCheck runs
        this._elementRef.nativeElement.type = 'radio';

        // if there's no id attribute set one
        if (!this._id) {
            this.setIdAttribute();
        }

        // register control valueAccessor
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        super.ngOnInit();

        // in case control value is null set the default one (isChecked) and sync Control State
        if (this.ngControl?.control?.value === null) {
            this.ngControl.control.setValue('', { emitModelToViewChange: false });
            // changing Model Expression after view checked, so detect changes
            // TODO: check why although it's checked .checked returns false
            // this.ngControl.viewToModelUpdate(this._checked);
            // this._cd.detectChanges();
        }
    }

    ngDoCheck() {
        this._hasLabel = document.querySelector(`[for='${this._id}']`) ? true : false;
        if (this.ngControl) {
            this.isInvalid = this.ngControl.invalid && this.ngControl.touched;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['label']) {
            // in case label Element already created update the value
            if (this._labelRef) {
                this._renderer.setProperty(this._labelRef, 'innerText', changes['label'].currentValue);
            } else {
                // create label Element
                this._labelRef = this._renderer.createElement('label');
                this._renderer.setProperty(this._labelRef, 'innerText', changes['label'].currentValue);
                this._renderer.setAttribute(this._labelRef, 'for', this._id);
                // append label element under the Radio
                const parent: HTMLElement = this._elementRef.nativeElement.parentElement;
                this._renderer.appendChild(parent, this._labelRef);
                this._renderer.insertBefore(parent, this._labelRef, this._elementRef.nativeElement.nextSibling);
            }
        }

        // in case id change then sync label for attribute
        if (changes['id'] && this._labelRef) {
            this._renderer.setAttribute(this._labelRef, 'for', changes['id'].currentValue);
        }

        // when readonly changes hide other radio (input+label)
        if (changes['readonly']) {
            // in readonly hide all input elements.
            if (this.readonly) {
                this._renderer.addClass(this._elementRef.nativeElement, 'eui-input-radio--hide');
            } else {
                this._renderer.removeClass(this._elementRef.nativeElement, 'eui-input-radio--hide');
            }
            this.processReadonly();
        }

        if (changes['isInvalid']) {
            if (changes['isInvalid'].currentValue) {
                this._renderer.addClass(this._elementRef.nativeElement, 'eui-input-radio--invalid');
            } else {
                this._renderer.removeClass(this._elementRef.nativeElement, 'eui-input-radio--invalid');
            }
        }
    }

    writeValue(obj: string): void {
        // set checked state based if radio value matches the control's one
        this._elementRef.nativeElement.checked = (this._elementRef.nativeElement.value === obj);
        this.processReadonly();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    @HostListener('change', ['$event.target.checked'])
    protected onCheckedChanged(checked) {
        this._defaultChecked = checked;
    }

    @HostListener('change', ['$event.target.value'])
    protected onValueChanged(value) {
        this.onChange(value);
    }

    protected setInvalid(state): void {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);

        // set BaseDirective Attribute
        super.euiDanger = this._isInvalid;
    }

    protected onChange = (_) => { /* Nothing to be Done so far */ };
    protected onTouched = (_) => { /* Nothing to be Done so far */ };

    private processReadonly() {
        const notRender = this._readonly && !this._elementRef.nativeElement.checked;
        // control the label that will be rendered
        const labelElementRef = document.querySelector(`[for=${this._id}]`);
        if (labelElementRef) {
            if (notRender) {
                this._renderer.setStyle(labelElementRef, 'display', 'none');
            } else {
                this._renderer.removeStyle(labelElementRef, 'display');
            }
        }
    }
}
